import { Button, ToggleButton } from '@heroui/react'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Glyph } from './Editor'
import { CALLED_DEFAULT_SECONDS, CALLED_MAX_SECONDS, CALLED_MIN_SECONDS, calledSeconds, hasCalled, removeCalled, withShowSec } from './calledScreen'
import { StudioSelect } from './heroParts'
import type { DisplayScreen, LayoutDocument } from './types'

/** The times offered for a Called screen, 5 s first among the short ones. */
export const SHOW_FOR_CHOICES: readonly number[] = [3, 4, 5, 6, 8, 10, 15, 20, 30]

/** A main display's screens in the left panel: the board, and its Called screen or a way to add one. */
export function DisplayTabs({
  document: doc,
  screen,
  onPick,
  onAdd,
}: {
  document: LayoutDocument
  screen: DisplayScreen
  onPick: (screen: DisplayScreen) => void
  onAdd: () => void
}) {
  const { say } = useStaffText()
  return (
    <div className="studio-screens" role="group" aria-label={say('designs.ed.displayScreen')}>
      <ToggleButton variant="ghost" size="sm" className="studio-navrow" isSelected={screen === 'board'} onPress={() => onPick('board')}>
        <Glyph of="calls" size={16} />
        <span className="studio-navrow__name">{say('designs.ed.default')}</span>
      </ToggleButton>
      {hasCalled(doc) ? (
        <ToggleButton variant="ghost" size="sm" className="studio-navrow" isSelected={screen === 'call'} onPress={() => onPick('call')}>
          <Glyph of="ticketNumber" size={16} />
          <span className="studio-navrow__name">{say('designs.ed.called')}</span>
        </ToggleButton>
      ) : (
        <Button variant="ghost" size="sm" className="studio-navrow studio-navrow--add" onPress={onAdd}>
          <Icon name="plus" size={16} />
          <span className="studio-navrow__name">{say('designs.ed.addCalled')}</span>
        </Button>
      )}
    </div>
  )
}

/** The Called screen's own settings: how long each call shows, and removing it. */
export function CalledPanel({ document: doc, onReplace }: { document: LayoutDocument; onReplace: (doc: LayoutDocument) => void }) {
  const { say } = useStaffText()
  const seconds = calledSeconds(doc)
  const choices = SHOW_FOR_CHOICES.includes(seconds) ? SHOW_FOR_CHOICES : [...SHOW_FOR_CHOICES, seconds].sort((a, b) => a - b)
  return (
    <section className="studio-panel">
      <div className="studio-panel__head">
        <h2>{say('designs.ed.called')}</h2>
      </div>
      <StudioSelect
          label={say('designs.ed.showFor')}
          value={String(seconds)}
          onChange={(value) => onReplace(withShowSec(doc, Number(value) || CALLED_DEFAULT_SECONDS))}
          options={choices
            .filter((n) => n >= CALLED_MIN_SECONDS && n <= CALLED_MAX_SECONDS)
            .map((n) => ({ value: String(n), label: say('designs.ed.secondsShort', { n }) }))}
      />
      <Button variant="danger-soft" size="sm" className="studio-panel__act" onPress={() => onReplace(removeCalled(doc))}>
        {say('designs.ed.removeCalled')}
      </Button>
    </section>
  )
}
